import { CoachCard } from '@efdigital/ll-addon-coaching';
import { getStatusIcon } from '@efdigital/ll-addon-coaching/utils/coaching-sessions/coach-partnership-status';
import type { CoachingPartnership } from '@efdigital/ll-addon-data';
import { Heading } from '@efdigital/ll-addon-ui';
import { EmptyState } from '@efdigital/ll-xceed-v2/components/application/empty-state';
import { Spinner } from '@efdigital/ll-xceed-v2/components/application/spinner.gts';
import { CoachPartnershipActions } from '@efdigital/ll-xceed-v2/components/coaching/coachee/coach-partnership-actions.gts';
import type CoachingCoacheeCohortCoachesIndexController from '@efdigital/ll-xceed-v2/controllers/coaching/coachee/cohort/coaches/index';
import { queryCoachProfilesByPartnerships } from '@efdigital/ll-xceed-v2/data/builders/coaching/coach-profiles';
import type { CoachingCoacheeCohortModel } from '@efdigital/ll-xceed-v2/routes/coaching/coachee/cohort';
import { coachStatusBadgeClass } from '@efdigital/ll-xceed-v2/utils/coaching/partnership-status-display';
import { PAGE_WRAPPER_CONTENT } from '@efdigital/ll-xceed-v2/utils/layout';
import { array, concat } from '@ember/helper';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import Component from '@glimmer/component';
import { Request } from '@warp-drive/ember';
import { pageTitle } from 'ember-page-title';

interface CoachesShortlistSignature {
  Args: {
    model: CoachingCoacheeCohortModel;
    controller: CoachingCoacheeCohortCoachesIndexController;
  };
}

const None = <template>
  <EmptyState @message="You have not shortlisted any coaches yet.">
    <:action>
      <LinkTo
        @route="coaching.coachee.cohort.coaches.index"
        class="btn btn-primary"
      >Browse Coaches</LinkTo>
    </:action>
  </EmptyState>
</template>;

function moreThanOne(count: number): boolean {
  return count > 1;
}

export default class CoachesShortlist extends Component<CoachesShortlistSignature> {
  queryShortlist(partnerships: CoachingPartnership[]) {
    return queryCoachProfilesByPartnerships(partnerships);
  }

  <template>
    {{pageTitle "Shortlist"}}

    <div class={{PAGE_WRAPPER_CONTENT}}>
      {{#if @model.planner.coachHasBeenChosen}}
        <Heading
          @size="sm"
          @text="Chemistry Matching Complete"
          @secondary="You have completed the chemistry matching process and have chosen a coach for your coaching journey."
          class="mb-4"
        />
      {{else}}
        <Heading
          @size="sm"
          @text="Chemistry Matching"
          @secondary="Once you have shortlisted a coach, you can book a chemistry session with them to decide if they are the right fit for you."
          class="mb-4"
        />

        {{! No `role="alert"` on the quota-exhausted branch: this text is part
            of the page on first render, and an alert is a live region for
            things that change after it. Announcing it on load either says
            nothing or cuts across the heading above it. }}
        <div
          class="alert
            {{if
              @model.planner.quota.chemistry.remaining
              'alert-light'
              'alert-warning'
            }}
            border d-flex align-items-start gap-3"
        >
          <iconify-icon
            inline
            icon={{if
              @model.planner.quota.chemistry.remaining
              "ph:info"
              "ph:warning"
            }}
            class="fs-4 flex-shrink-0"
            aria-hidden="true"
          />
          <div>
            {{#if (moreThanOne @model.planner.quota.chemistry.remaining)}}
              <p>You can book up to
                {{@model.planner.quota.chemistry.remaining}}
                more chemistry sessions.</p>
            {{else if @model.planner.quota.chemistry.remaining}}
              <p>You can book one more chemistry session.</p>
            {{else}}
              <p>You have reached the maximum number of chemistry sessions. You
                must now decide which coach you want to work with.</p>
            {{/if}}
            <p class="mb-0">Once you have completed a chemistry session, you
              confirm your coach and start your coaching journey with them.</p>
          </div>
        </div>
      {{/if}}

      {{#if @model.planner.chemistryAndChosenPartnerships.length}}
        <Request
          @query={{this.queryShortlist
            @model.planner.chemistryAndChosenPartnerships
          }}
        >
          <:content as |coachProfiles|>
            {{#if coachProfiles.data.length}}
              <div class="row g-3 g-xl-4 align-items-stretch">
                {{#each coachProfiles.data as |coachProfile|}}

                  {{#let
                    (@model.planner.getStatusForCoach coachProfile.user)
                    as |status|
                  }}
                    {{#let
                      (@model.planner.hasUncancelledChemistrySession
                        coachProfile.user
                      )
                      as |hasChemistrySession|
                    }}
                      {{#let
                        (getStatusIcon status hasChemistrySession)
                        as |icon|
                      }}
                        <div class="col-12 col-md-6 col-xxl-4">

                          <CoachCard
                            class="h-100 w-100 ll-coach-browse-card"
                            @coach={{coachProfile}}
                            @cardClass="h-100 w-100 rounded-4 shadow-sm"
                            @footerClass="d-flex flex-row justify-content-between gap-2 align-items-center bg-transparent border-top"
                            @color={{icon.color}}
                          >
                            <:badge>
                              {{#if icon}}
                                {{#let
                                  (coachStatusBadgeClass icon)
                                  as |badgeClass|
                                }}
                                  <span class={{badgeClass}}>
                                    <iconify-icon
                                      icon="{{icon.icon}}"
                                      class="fs-6"
                                      inline
                                      aria-hidden="true"
                                    />
                                    {{icon.label}}
                                  </span>
                                {{/let}}
                              {{/if}}
                            </:badge>
                            <:actionsBack>
                              <CoachPartnershipActions
                                @planner={{@model.planner}}
                                @coachUserId={{coachProfile.user}}
                                @coachName={{concat
                                  coachProfile.firstName
                                  " "
                                  coachProfile.lastName
                                }}
                                @buttonClass="btn btn-sm rounded-pill text-nowrap"
                                @collapseMultipleActions={{true}}
                              />
                            </:actionsBack>
                            <:actionsFront>
                              <LinkTo
                                @route="coaching.coachee.cohort.coaches.coach"
                                @models={{array
                                  @model.program_id
                                  @model.cohort_id
                                  coachProfile.user
                                }}
                                class="btn btn-outline-primary btn-sm rounded-pill d-inline-flex align-items-center justify-content-center gap-1 text-nowrap"
                              >Full profile
                                <iconify-icon
                                  icon="ph:arrow-right-bold"
                                  inline
                                  aria-hidden="true"
                                /></LinkTo>
                            </:actionsFront>
                          </CoachCard>
                        </div>
                      {{/let}}
                    {{/let}}
                  {{/let}}
                {{/each}}
              </div>
            {{else}}
              <None />
            {{/if}}
          </:content>
          <:loading>
            <Spinner />
          </:loading>
          <:error as |_error state|>
            <div class="alert alert-danger" role="alert">
              <p>Shortlisted coaches could not be loaded.</p>
              <button
                type="button"
                class="btn btn-sm btn-outline-primary"
                {{on "click" state.retry}}
              >Try again</button>
            </div>
          </:error>
        </Request>
      {{else}}
        <None />
      {{/if}}
    </div>
  </template>
}
